'use client';

import { useEffect, useMemo, useRef } from 'react';
import {
  isChunkLoadError,
  resolveChunkReloadSafety,
  tryReloadForChunkError,
  type ChunkLoadRecovery,
} from '@/lib/chunk-load-recovery';
import { reportUserBlockingFailure } from '@/lib/observability/user-blocking-failure-reporter';
import { getReloadSafety } from '@/lib/reload-blockers';

/**
 * What a chunk-load crash reports. Nothing goes through posthog-js here (its
 * events carry the page URL, person and session properties): a successful
 * auto-reload is silent, and the blocking fallback screen is the one minimal
 * page-crash beacon (error class, digest, route pattern, asset file) that
 * `reportUserBlockingFailure` sends on its own (#7286, #7292).
 */
function reportRecovery(outcome: ChunkLoadRecovery, error: unknown): void {
  if (outcome === 'reloaded') return;
  // The fallback screen is the only user-blocking outcome (#6041): it goes
  // to #alerting. The screen claims an update rather than the real cause,
  // so it is not an explained state.
  reportUserBlockingFailure({
    surface: 'page_crash',
    operation: `chunk_load_fallback/${outcome}`,
    error,
  });
}

/**
 * Reloads once on a chunk-load crash when the route was safe to leave. Call it
 * from the screen a boundary renders: the safety snapshot is taken in render,
 * before the crashed route's reload owners unmount.
 */
export function useChunkLoadRecovery(error: unknown): void {
  const chunkError = error !== undefined && isChunkLoadError(error);
  const safetyAtCrash = useMemo(
    () => (chunkError ? getReloadSafety() : 'unknown'),
    [chunkError]
  );
  const handled = useRef(false);
  useEffect(() => {
    if (!chunkError || handled.current) return;
    handled.current = true;
    const path = window.location.pathname;
    let outcome: ChunkLoadRecovery;
    try {
      outcome = tryReloadForChunkError({
        path,
        safety: resolveChunkReloadSafety(safetyAtCrash, getReloadSafety()),
        storage: window.sessionStorage,
        reload: () => window.location.reload(),
      });
    } catch {
      // Reading window.sessionStorage can itself throw. Keep the manual screen.
      outcome = 'storage';
    }
    reportRecovery(outcome, error);
  }, [chunkError, error, safetyAtCrash]);
}
